import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

// One panel position update per frame to avoid 30+ layout reads when many panels update together.
const positionUpdateQueue = new Set();
let positionUpdateScheduled = false;
function processPositionUpdateQueue() {
  positionUpdateScheduled = false;
  const next = positionUpdateQueue.values().next().value;
  if (next == null) return;
  positionUpdateQueue.delete(next);
  if (next.isConnected && typeof next.runPositionUpdate === 'function') next.runPositionUpdate();
  if (positionUpdateQueue.size > 0) {
    positionUpdateScheduled = true;
    requestAnimationFrame(processPositionUpdateQueue);
  }
}

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  /** Called by the global queue (one per frame). */
  runPositionUpdate() {
    if (this.isConnected) this.#updatePosition();
  }

  #schedulePositionUpdate = () => {
    positionUpdateQueue.add(this);
    if (!positionUpdateScheduled) {
      positionUpdateScheduled = true;
      requestAnimationFrame(processPositionUpdateQueue);
    }
  };

  #mutationObserver = new MutationObserver(this.#schedulePositionUpdate);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#schedulePositionUpdate();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#schedulePositionUpdate();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
